import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  LinearProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  Avatar,
  Badge,
  Tabs,
  Tab,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  Stepper,
  Step,
  StepLabel,
  Divider,
  Stack,
} from '@mui/material';
import {
  PendingActions,
  CheckCircle,
  Cancel,
  ThumbUp,
  ThumbDown,
  Visibility,
  History,
  Person,
  AttachMoney,
  AccountBalance,
  SwapHoriz,
  Settings,
  Warning,
  Info,
  Schedule,
  Comment,
  Assignment,
  AssignmentTurnedIn,
  Group,
  Refresh,
  FilterList,
  CheckBox as CheckBoxIcon,
  CheckBoxOutlineBlank,
} from '@mui/icons-material';
import { format, formatDistanceToNow } from 'date-fns';
import { approvalService } from '../services/api';
import NotificationSnackbar from '../components/NotificationSnackbar';

interface ApprovalRequest {
  id: string;
  type: 'REFUND' | 'SETTLEMENT' | 'PARTNER_CHANGE' | 'COMMISSION_CHANGE' | 'VOUCHER_REACTIVATION' | 'BANK_ACCOUNT_CHANGE';
  status: 'PENDING' | 'APPROVED' | 'REJECTED' | 'ESCALATED';
  priority: 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';
  requestedBy: string;
  requestedDate: string;
  description: string;
  amount?: number;
  currency?: string;
  entityId: string;
  entityName: string;
  requiredApprovers: number;
  currentApprovers: string[];
  comments: Array<{
    user: string;
    comment: string;
    timestamp: string;
    action: 'COMMENT' | 'APPROVED' | 'REJECTED';
  }>;
  metadata: any;
  expiryDate?: string;
}

const ApprovalQueue: React.FC = () => {
  const [approvals, setApprovals] = useState<ApprovalRequest[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedApproval, setSelectedApproval] = useState<ApprovalRequest | null>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [actionDialog, setActionDialog] = useState(false);
  const [comment, setComment] = useState('');
  const [tabValue, setTabValue] = useState(0);
  const [selectedApprovals, setSelectedApprovals] = useState<string[]>([]);
  const [filterType, setFilterType] = useState('ALL');
  const [filterPriority, setFilterPriority] = useState('ALL');
  const [notification, setNotification] = useState<{
    open: boolean;
    message: string;
    type: 'success' | 'error' | 'warning' | 'info';
  }>({
    open: false,
    message: '',
    type: 'success',
  });
  const [tabCounts, setTabCounts] = useState({
    all: 0,
    highPriority: 0,
    inProgress: 0,
    expiringSoon: 0,
  });

  // Statistics
  const [stats, setStats] = useState({
    pendingCount: 0,
    approvedToday: 0,
    rejectedToday: 0,
    avgApprovalTime: '0 hours',
    criticalPending: 0,
    expiringToday: 0,
  });

  useEffect(() => {
    fetchApprovals();
    fetchStatistics();
  }, [tabValue, filterType, filterPriority]);

  const fetchStatistics = async () => {
    try {
      // Fetch all pending approvals for statistics
      const response = await approvalService.getApprovalQueue({ status: 'PENDING' });
      const allPending = response.content || [];

      // Calculate statistics
      const pending = allPending.length;
      const critical = allPending.filter((a: ApprovalRequest) => a.priority === 'CRITICAL').length;

      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const tomorrow = new Date(today);
      tomorrow.setDate(tomorrow.getDate() + 1);

      const expiring = allPending.filter((a: ApprovalRequest) => {
        if (!a.expiryDate) return false;
        const expiryDate = new Date(a.expiryDate);
        return expiryDate >= today && expiryDate < tomorrow;
      }).length;

      // Calculate tab counts
      const highPriority = allPending.filter((a: ApprovalRequest) =>
        a.priority === 'CRITICAL' || a.priority === 'HIGH'
      ).length;
      const inProgress = allPending.filter((a: ApprovalRequest) =>
        a.currentApprovers && a.currentApprovers.length > 0
      ).length;

      setTabCounts({
        all: pending,
        highPriority,
        inProgress,
        expiringSoon: expiring,
      });

      // Fetch today's approved/rejected (if backend supports it)
      try {
        const approvedResponse = await approvalService.getApprovalQueue({
          status: 'APPROVED',
          fromDate: today.toISOString(),
        });
        const rejectedResponse = await approvalService.getApprovalQueue({
          status: 'REJECTED',
          fromDate: today.toISOString(),
        });

        setStats({
          pendingCount: pending,
          approvedToday: approvedResponse.totalElements || 0,
          rejectedToday: rejectedResponse.totalElements || 0,
          avgApprovalTime: '2.5 hours', // TODO: Calculate from backend data
          criticalPending: critical,
          expiringToday: expiring,
        });
      } catch (err) {
        // If backend doesn't support filtering by date, just update what we have
        setStats({
          pendingCount: pending,
          approvedToday: 0,
          rejectedToday: 0,
          avgApprovalTime: 'N/A',
          criticalPending: critical,
          expiringToday: expiring,
        });
      }
    } catch (error) {
      console.error('Failed to fetch statistics:', error);
    }
  };

  const fetchApprovals = async () => {
    setLoading(true);
    try {
      const params: any = {
        status: tabValue === 0 ? 'PENDING' : undefined,
      };

      // Filter by type
      if (filterType !== 'ALL') {
        params.type = filterType;
      }

      // Filter by priority
      if (filterPriority !== 'ALL') {
        params.priority = filterPriority;
      }

      const response = await approvalService.getApprovalQueue(params);
      let filtered = response.content || [];

      // Apply tab-specific filters
      if (tabValue === 1) {
        // High Priority
        filtered = filtered.filter((a: ApprovalRequest) => a.priority === 'CRITICAL' || a.priority === 'HIGH');
      } else if (tabValue === 2) {
        // In Progress (has at least one approver)
        filtered = filtered.filter((a: ApprovalRequest) => a.currentApprovers && a.currentApprovers.length > 0);
      } else if (tabValue === 3) {
        // Expiring Soon (within 24 hours)
        const tomorrow = new Date();
        tomorrow.setHours(tomorrow.getHours() + 24);
        filtered = filtered.filter((a: ApprovalRequest) =>
          a.expiryDate && new Date(a.expiryDate) < tomorrow
        );
      }

      setApprovals(filtered);
    } catch (error) {
      console.error('Failed to fetch approvals:', error);
      setApprovals([]);
    } finally {
      setLoading(false);
    }
  };

  const showNotification = (message: string, type: 'success' | 'error' | 'warning' | 'info' = 'success') => {
    setNotification({ open: true, message, type });
  };

  const handleApprove = async () => {
    if (!selectedApproval) return;

    try {
      await approvalService.approveRequest(selectedApproval.id, comment);
      showNotification('Request approved successfully', 'success');
      setActionDialog(false);
      setComment('');
      setSelectedApproval(null);
      fetchApprovals();
      fetchStatistics();
    } catch (error: any) {
      console.error('Failed to approve request:', error);
      showNotification(error.response?.data?.message || 'Failed to approve request', 'error');
    }
  };

  const handleReject = async () => {
    if (!selectedApproval || !comment) {
      showNotification('Please provide a reason for rejection', 'warning');
      return;
    }

    try {
      await approvalService.rejectRequest(selectedApproval.id, comment);
      showNotification('Request rejected successfully', 'success');
      setActionDialog(false);
      setComment('');
      setSelectedApproval(null);
      fetchApprovals();
      fetchStatistics();
    } catch (error: any) {
      console.error('Failed to reject request:', error);
      showNotification(error.response?.data?.message || 'Failed to reject request', 'error');
    }
  };

  const handleBulkApprove = async () => {
    if (selectedApprovals.length === 0) {
      showNotification('Please select requests to approve', 'warning');
      return;
    }

    if (!window.confirm(`Are you sure you want to approve ${selectedApprovals.length} request(s)?`)) {
      return;
    }

    try {
      await approvalService.bulkApprove(selectedApprovals, 'Bulk approved');
      showNotification(`${selectedApprovals.length} request(s) approved successfully`, 'success');
      setSelectedApprovals([]);
      fetchApprovals();
      fetchStatistics();
    } catch (error: any) {
      console.error('Failed to bulk approve:', error);
      showNotification(error.response?.data?.message || 'Failed to bulk approve requests', 'error');
    }
  };

  const getTypeIcon = (type: ApprovalRequest['type']) => {
    switch (type) {
      case 'REFUND': return <AttachMoney />;
      case 'SETTLEMENT': return <AccountBalance />;
      case 'PARTNER_CHANGE': return <Group />;
      case 'COMMISSION_CHANGE': return <SwapHoriz />;
      case 'VOUCHER_REACTIVATION': return <Assignment />;
      case 'BANK_ACCOUNT_CHANGE': return <AccountBalance />;
      default: return <PendingActions />;
    }
  };

  const getPriorityColor = (priority: ApprovalRequest['priority']) => {
    switch (priority) {
      case 'CRITICAL': return 'error';
      case 'HIGH': return 'warning';
      case 'MEDIUM': return 'info';
      case 'LOW': return 'default';
      default: return 'default';
    }
  };

  const getApprovalProgress = (approval: ApprovalRequest) => {
    return (approval.currentApprovers.length / approval.requiredApprovers) * 100;
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
        <Box>
          <Typography variant="h4" gutterBottom>
            Approval Queue
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Maker-Checker workflow system for multi-level authorization of critical operations
          </Typography>
        </Box>
        <Box display="flex" gap={2}>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={fetchApprovals}
          >
            Refresh
          </Button>
          {selectedApprovals.length > 0 && (
            <Button
              variant="contained"
              color="success"
              startIcon={<CheckCircle />}
              onClick={handleBulkApprove}
            >
              Bulk Approve ({selectedApprovals.length})
            </Button>
          )}
        </Box>
      </Box>

      {/* Information Section */}
      <Alert severity="info" sx={{ mb: 3 }}>
        <Typography variant="subtitle2" gutterBottom>
          <strong>What is the Approval Queue?</strong>
        </Typography>
        <Typography variant="body2" paragraph>
          The Approval Queue implements a <strong>Maker-Checker</strong> control system that requires multiple levels of
          authorization before critical operations can be executed.
        </Typography>
        <Typography variant="subtitle2" gutterBottom>
          <strong>Approval Types:</strong>
        </Typography>
        <Box component="ul" sx={{ mt: 1, mb: 0, pl: 2 }}>
          <li><strong>Refunds</strong> - Customer refund requests for vouchers require approval before processing</li>
          <li><strong>Settlements</strong> - High-value settlement batches need multi-level approval</li>
          <li><strong>Partner Changes</strong> - Updates to partner information (e.g., contact details, business info)</li>
          <li><strong>Commission Changes</strong> - Adjustments to partner commission rates</li>
          <li><strong>Bank Account Changes</strong> - Updates to partner settlement bank accounts</li>
          <li><strong>Voucher Reactivation</strong> - Reactivating expired or cancelled vouchers</li>
        </Box>
      </Alert>

      {/* Statistics Cards */}
      <Grid container spacing={3} mb={3}>
        <Grid item xs={12} sm={6} md={2}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom variant="caption">
                Pending
              </Typography>
              <Typography variant="h4" color="warning.main">
                {stats.pendingCount}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom variant="caption">
                Critical
              </Typography>
              <Typography variant="h4" color="error.main">
                {stats.criticalPending}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom variant="caption">
                Expiring Today
              </Typography>
              <Typography variant="h4" color="warning.main">
                {stats.expiringToday}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom variant="caption">
                Approved Today
              </Typography>
              <Typography variant="h4" color="success.main">
                {stats.approvedToday}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom variant="caption">
                Rejected Today
              </Typography>
              <Typography variant="h4" color="error.main">
                {stats.rejectedToday}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom variant="caption">
                Avg Time
              </Typography>
              <Typography variant="h6">
                {stats.avgApprovalTime}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters */}
      <Paper sx={{ mb: 3, p: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth size="small">
              <InputLabel>Type</InputLabel>
              <Select
                value={filterType}
                label="Type"
                onChange={(e) => setFilterType(e.target.value)}
              >
                <MenuItem value="ALL">All Types</MenuItem>
                <MenuItem value="REFUND">Refund</MenuItem>
                <MenuItem value="SETTLEMENT">Settlement</MenuItem>
                <MenuItem value="PARTNER_CHANGE">Partner Change</MenuItem>
                <MenuItem value="COMMISSION_CHANGE">Commission Change</MenuItem>
                <MenuItem value="BANK_ACCOUNT_CHANGE">Bank Account Change</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth size="small">
              <InputLabel>Priority</InputLabel>
              <Select
                value={filterPriority}
                label="Priority"
                onChange={(e) => setFilterPriority(e.target.value)}
              >
                <MenuItem value="ALL">All Priorities</MenuItem>
                <MenuItem value="CRITICAL">Critical</MenuItem>
                <MenuItem value="HIGH">High</MenuItem>
                <MenuItem value="MEDIUM">Medium</MenuItem>
                <MenuItem value="LOW">Low</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)}>
          <Tab
            label="All Requests"
            icon={<Badge badgeContent={tabCounts.all} color="primary" />}
          />
          <Tab
            label="High Priority"
            icon={<Badge badgeContent={tabCounts.highPriority} color="error" />}
          />
          <Tab
            label="In Progress"
            icon={<Badge badgeContent={tabCounts.inProgress} color="info" />}
          />
          <Tab
            label="Expiring Soon"
            icon={<Badge badgeContent={tabCounts.expiringSoon} color="warning" />}
          />
        </Tabs>
      </Paper>

      {/* Approvals Table */}
      <Paper>
        {loading && <LinearProgress />}
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    indeterminate={selectedApprovals.length > 0 && selectedApprovals.length < approvals.length}
                    checked={selectedApprovals.length === approvals.length}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedApprovals(approvals.map(a => a.id));
                      } else {
                        setSelectedApprovals([]);
                      }
                    }}
                  />
                </TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Description</TableCell>
                <TableCell>Requested By</TableCell>
                <TableCell>Priority</TableCell>
                <TableCell>Amount</TableCell>
                <TableCell>Approval Progress</TableCell>
                <TableCell>Age</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {approvals.map((approval) => (
                <TableRow key={approval.id}>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={selectedApprovals.includes(approval.id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedApprovals([...selectedApprovals, approval.id]);
                        } else {
                          setSelectedApprovals(selectedApprovals.filter(id => id !== approval.id));
                        }
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={1}>
                      {getTypeIcon(approval.type)}
                      <Typography variant="body2">
                        {approval.type.replace(/_/g, ' ')}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box>
                      <Typography variant="body2">{approval.description}</Typography>
                      <Typography variant="caption" color="textSecondary">
                        {approval.entityName}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Avatar sx={{ width: 24, height: 24 }}>
                        <Person fontSize="small" />
                      </Avatar>
                      <Typography variant="body2">
                        {approval.requestedBy}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={approval.priority}
                      color={getPriorityColor(approval.priority) as any}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    {approval.amount && (
                      <Typography fontWeight="bold">
                        {approval.currency} {approval.amount.toLocaleString()}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Box>
                      <Box display="flex" alignItems="center" gap={1}>
                        <LinearProgress
                          variant="determinate"
                          value={getApprovalProgress(approval)}
                          sx={{ flex: 1, height: 8, borderRadius: 4 }}
                        />
                        <Typography variant="caption">
                          {approval.currentApprovers.length}/{approval.requiredApprovers}
                        </Typography>
                      </Box>
                      {approval.expiryDate && (
                        <Typography variant="caption" color="warning.main">
                          Expires {formatDistanceToNow(new Date(approval.expiryDate), { addSuffix: true })}
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption">
                      {formatDistanceToNow(new Date(approval.requestedDate), { addSuffix: true })}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Box display="flex" gap={0.5} justifyContent="center">
                      <Tooltip title="View Details">
                        <IconButton
                          size="small"
                          onClick={() => {
                            setSelectedApproval(approval);
                            setDetailsOpen(true);
                          }}
                        >
                          <Visibility fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Take Action">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => {
                            setSelectedApproval(approval);
                            setActionDialog(true);
                          }}
                        >
                          <PendingActions fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Details Dialog */}
      <Dialog
        open={detailsOpen}
        onClose={() => setDetailsOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Approval Request Details
          {selectedApproval && (
            <Chip
              label={selectedApproval.priority}
              color={getPriorityColor(selectedApproval.priority) as any}
              size="small"
              sx={{ ml: 2 }}
            />
          )}
        </DialogTitle>
        <DialogContent>
          {selectedApproval && (
            <>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="textSecondary">
                    Request Type
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    {selectedApproval.type.replace(/_/g, ' ')}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="textSecondary">
                    Requested By
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    {selectedApproval.requestedBy}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="caption" color="textSecondary">
                    Description
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    {selectedApproval.description}
                  </Typography>
                </Grid>
                {selectedApproval.amount && (
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="textSecondary">
                      Amount
                    </Typography>
                    <Typography variant="body1" fontWeight="bold" gutterBottom>
                      {selectedApproval.currency} {selectedApproval.amount.toLocaleString()}
                    </Typography>
                  </Grid>
                )}
              </Grid>

              <Divider sx={{ my: 2 }} />

              <Typography variant="h6" gutterBottom>
                Approval Progress
              </Typography>
              <Stepper activeStep={selectedApproval.currentApprovers.length}>
                {Array.from({ length: selectedApproval.requiredApprovers }).map((_, index) => (
                  <Step key={index}>
                    <StepLabel>
                      {selectedApproval.currentApprovers[index] || `Approver ${index + 1}`}
                    </StepLabel>
                  </Step>
                ))}
              </Stepper>

              {selectedApproval.comments.length > 0 && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="h6" gutterBottom>
                    Comments & Actions
                  </Typography>
                  <List>
                    {selectedApproval.comments.map((comment, index) => (
                      <ListItem key={index}>
                        <ListItemIcon>
                          {comment.action === 'APPROVED' ? (
                            <CheckCircle color="success" />
                          ) : comment.action === 'REJECTED' ? (
                            <Cancel color="error" />
                          ) : (
                            <Comment />
                          )}
                        </ListItemIcon>
                        <ListItemText
                          primary={comment.comment}
                          secondary={`${comment.user} - ${format(new Date(comment.timestamp), 'PPpp')}`}
                        />
                      </ListItem>
                    ))}
                  </List>
                </>
              )}

              {selectedApproval.metadata && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="h6" gutterBottom>
                    Additional Details
                  </Typography>
                  <Grid container spacing={1}>
                    {Object.entries(selectedApproval.metadata).map(([key, value]) => (
                      <Grid item xs={12} sm={6} key={key}>
                        <Typography variant="caption" color="textSecondary">
                          {key.replace(/([A-Z])/g, ' $1').trim()}
                        </Typography>
                        <Typography variant="body2">
                          {String(value)}
                        </Typography>
                      </Grid>
                    ))}
                  </Grid>
                </>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailsOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Action Dialog */}
      <Dialog
        open={actionDialog}
        onClose={() => setActionDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Take Action on Request
        </DialogTitle>
        <DialogContent>
          {selectedApproval && (
            <>
              <Alert severity="info" sx={{ mb: 2 }}>
                You are about to take action on: <strong>{selectedApproval.description}</strong>
              </Alert>
              <TextField
                fullWidth
                label="Comments (Required for Rejection)"
                multiline
                rows={4}
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder="Please provide your comments or reason for this action..."
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setActionDialog(false)}>Cancel</Button>
          <Button
            variant="outlined"
            color="error"
            startIcon={<ThumbDown />}
            onClick={handleReject}
          >
            Reject
          </Button>
          <Button
            variant="contained"
            color="success"
            startIcon={<ThumbUp />}
            onClick={handleApprove}
          >
            Approve
          </Button>
        </DialogActions>
      </Dialog>

      {/* Notification */}
      <NotificationSnackbar
        open={notification.open}
        message={notification.message}
        type={notification.type}
        onClose={() => setNotification({ ...notification, open: false })}
      />
    </Box>
  );
};

export default ApprovalQueue;